import {
  computeFreshness,
  parseCanonicalTransaction,
  parseDashboardManifest,
  validateLoadedDataset
} from "./dashboard-schema";
import type {
  CanonicalTransaction,
  DashboardIndexes,
  DashboardManifest,
  DatasetHealth,
  LoadedDashboardData
} from "./types";

export const DASHBOARD_DATA_URL = "/data/insider-dashboard.json";
const DASHBOARD_DATA_BASE_URL = DASHBOARD_DATA_URL.slice(0, DASHBOARD_DATA_URL.lastIndexOf("/") + 1);

export type DashboardDataErrorCode =
  | "manifest_unavailable"
  | "manifest_http"
  | "manifest_invalid"
  | "dataset_invalid"
  | "shard_unavailable"
  | "shard_http"
  | "shard_invalid"
  | "shard_hash_mismatch"
  | "dataset_validation_failed";

interface DashboardDataErrorContext {
  manifest?: DashboardManifest;
  shardUrl?: string;
  shardIndex?: number;
  rowIndex?: number;
  httpStatus?: number;
  cause?: unknown;
}

export class DashboardDataError extends Error {
  readonly code: DashboardDataErrorCode;
  readonly manifest?: DashboardManifest;
  readonly shardUrl?: string;
  readonly shardIndex?: number;
  readonly rowIndex?: number;
  readonly httpStatus?: number;
  readonly cause?: unknown;

  constructor(code: DashboardDataErrorCode, message: string, context: DashboardDataErrorContext = {}) {
    super(message);
    this.name = "DashboardDataError";
    this.code = code;
    this.manifest = context.manifest;
    this.shardUrl = context.shardUrl;
    this.shardIndex = context.shardIndex;
    this.rowIndex = context.rowIndex;
    this.httpStatus = context.httpStatus;
    this.cause = context.cause;
  }
}

function appendIndex(
  index: Map<string, CanonicalTransaction[]>,
  key: string,
  row: CanonicalTransaction
): void {
  const existing = index.get(key);
  if (existing) existing.push(row);
  else index.set(key, [row]);
}

function compareIds(left: CanonicalTransaction, right: CanonicalTransaction): number {
  return left.transactionId < right.transactionId
    ? -1
    : left.transactionId > right.transactionId
      ? 1
      : 0;
}

function compareNullableDate(
  left: string | null,
  right: string | null,
  descending: boolean
): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  if (left === right) return 0;
  const comparison = left < right ? -1 : 1;
  return descending ? -comparison : comparison;
}

function compareRecentHistory(left: CanonicalTransaction, right: CanonicalTransaction): number {
  if (left.filingDate !== right.filingDate) {
    return left.filingDate < right.filingDate ? 1 : -1;
  }
  const trade = compareNullableDate(left.tradeDate, right.tradeDate, true);
  return trade !== 0 ? trade : compareIds(left, right);
}

function compareClusterHistory(left: CanonicalTransaction, right: CanonicalTransaction): number {
  const trade = compareNullableDate(left.tradeDate, right.tradeDate, false);
  return trade !== 0 ? trade : compareIds(left, right);
}

function sortIndexBuckets(
  index: Map<string, CanonicalTransaction[]>,
  compare: (left: CanonicalTransaction, right: CanonicalTransaction) => number
): void {
  for (const bucket of index.values()) bucket.sort(compare);
}

export function buildDashboardIndexes(rows: CanonicalTransaction[]): DashboardIndexes {
  const indexes: DashboardIndexes = {
    byId: new Map(),
    byIssuer: new Map(),
    byOwner: new Map(),
    byAccession: new Map(),
    byCluster: new Map()
  };

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex]!;
    if (indexes.byId.has(row.transactionId)) {
      throw new DashboardDataError(
        "dataset_validation_failed",
        `Duplicate canonical transaction ID at row ${rowIndex}: ${row.transactionId}`,
        { rowIndex }
      );
    }
    indexes.byId.set(row.transactionId, row);
    appendIndex(indexes.byIssuer, row.issuerCik, row);
    if (row.ownerIdentityKey !== null && row.ownerIdentityKey.trim() !== "") {
      appendIndex(indexes.byOwner, row.ownerIdentityKey, row);
    }
    appendIndex(indexes.byAccession, row.accessionNumber, row);
    if (row.cluster !== null) appendIndex(indexes.byCluster, row.cluster.clusterId, row);
  }

  sortIndexBuckets(indexes.byIssuer, compareRecentHistory);
  sortIndexBuckets(indexes.byOwner, compareRecentHistory);
  sortIndexBuckets(indexes.byAccession, compareRecentHistory);
  sortIndexBuckets(indexes.byCluster, compareClusterHistory);

  return indexes;
}

function resolveCanonicalShardUrl(shardName: string): string {
  const syntheticOrigin = "https://dashboard.invalid";
  const manifestUrl = new URL(DASHBOARD_DATA_URL, syntheticOrigin);
  const resolved = new URL(shardName, manifestUrl);
  const expectedPath = `${DASHBOARD_DATA_BASE_URL}${shardName}`;
  if (
    resolved.origin !== syntheticOrigin ||
    resolved.pathname !== expectedPath ||
    resolved.search !== "" ||
    resolved.hash !== "" ||
    shardName.includes("/") ||
    shardName.includes("\\")
  ) {
    throw new DashboardDataError("shard_invalid", `Unsafe dashboard shard URL: ${shardName}`, {
      shardUrl: shardName
    });
  }
  return expectedPath;
}

async function contentHashPrefix(payload: Uint8Array<ArrayBuffer>): Promise<string> {
  if (typeof globalThis.crypto?.subtle?.digest !== "function") {
    throw new Error("Web Crypto SHA-256 is unavailable");
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", payload);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 12);
}

async function loadCanonicalShard(
  fetcher: typeof fetch,
  shardName: string,
  shardIndex: number
): Promise<CanonicalTransaction[]> {
  const shardUrl = resolveCanonicalShardUrl(shardName);
  let response: Response;
  try {
    response = await fetcher(shardUrl, { cache: "force-cache" });
  } catch (cause) {
    throw new DashboardDataError(
      "shard_unavailable",
      `Unable to fetch dashboard shard ${shardIndex}: ${shardUrl}`,
      { shardUrl, shardIndex, cause }
    );
  }

  if (!response.ok) {
    throw new DashboardDataError(
      "shard_http",
      `Dashboard shard ${shardIndex} returned HTTP ${response.status}: ${shardUrl}`,
      { shardUrl, shardIndex, httpStatus: response.status }
    );
  }

  let rawPayload: Uint8Array<ArrayBuffer>;
  try {
    rawPayload = new Uint8Array(await response.arrayBuffer());
  } catch (cause) {
    throw new DashboardDataError(
      "shard_invalid",
      `Dashboard shard ${shardIndex} could not be read: ${shardUrl}`,
      { shardUrl, shardIndex, cause }
    );
  }
  const expectedHash = shardName.match(/-([0-9a-f]{12})\.json$/)?.[1];
  try {
    const actualHash = await contentHashPrefix(rawPayload);
    if (actualHash !== expectedHash) {
      throw new DashboardDataError(
        "shard_hash_mismatch",
        `Dashboard shard ${shardIndex} failed its content hash: ${shardUrl}`,
        { shardUrl, shardIndex }
      );
    }
  } catch (cause) {
    if (cause instanceof DashboardDataError) throw cause;
    throw new DashboardDataError(
      "shard_invalid",
      `Dashboard shard ${shardIndex} could not be content-verified: ${shardUrl}`,
      { shardUrl, shardIndex, cause }
    );
  }
  let decodedPayload: string;
  try {
    decodedPayload = new TextDecoder("utf-8", { fatal: true }).decode(rawPayload);
  } catch (cause) {
    throw new DashboardDataError(
      "shard_invalid",
      `Dashboard shard ${shardIndex} is not valid UTF-8: ${shardUrl}`,
      { shardUrl, shardIndex, cause }
    );
  }
  let payload: unknown;
  try {
    payload = JSON.parse(decodedPayload);
  } catch (cause) {
    throw new DashboardDataError(
      "shard_invalid",
      `Dashboard shard ${shardIndex} is not valid JSON: ${shardUrl}`,
      { shardUrl, shardIndex, cause }
    );
  }
  if (!Array.isArray(payload)) {
    throw new DashboardDataError(
      "shard_invalid",
      `Dashboard shard ${shardIndex} must contain an array: ${shardUrl}`,
      { shardUrl, shardIndex }
    );
  }

  const rows: CanonicalTransaction[] = [];
  for (let rowIndex = 0; rowIndex < payload.length; rowIndex += 1) {
    try {
      rows.push(parseCanonicalTransaction(payload[rowIndex]));
    } catch (cause) {
      throw new DashboardDataError(
        "shard_invalid",
        `Invalid dashboard row ${rowIndex} in shard ${shardIndex}: ${shardUrl}`,
        { shardUrl, shardIndex, rowIndex, cause }
      );
    }
  }
  return rows;
}

function healthFromManifest(manifest: DashboardManifest, now: Date): DatasetHealth {
  const freshness = computeFreshness(manifest, now);
  const generatedAtMs = Date.parse(manifest.generatedAt);
  const generatedAgeHours = Number.isFinite(generatedAtMs)
    ? Math.max(0, (now.getTime() - generatedAtMs) / 3_600_000)
    : null;
  const isLiveTrusted =
    manifest.datasetMode === "live" &&
    manifest.ingestState === "completed" &&
    manifest.validityState === "valid" &&
    freshness === "fresh" &&
    !manifest.snapshotFromLastCompletedRun;
  return {
    datasetMode: manifest.datasetMode,
    ingestState: manifest.ingestState,
    validityState: manifest.validityState,
    freshness,
    snapshotFromLastCompletedRun: manifest.snapshotFromLastCompletedRun,
    criticalErrorCount: manifest.criticalErrorCount,
    warningCount: manifest.warningCount,
    validationIssues: manifest.validationIssues,
    isLiveTrusted,
    generatedAgeHours
  };
}

export async function loadDashboardData(
  fetcher: typeof fetch | undefined = globalThis.fetch,
  now = new Date()
): Promise<LoadedDashboardData> {
  if (typeof fetcher !== "function") {
    throw new DashboardDataError(
      "manifest_unavailable",
      "Dashboard manifest cannot be loaded because fetch is unavailable"
    );
  }

  let response: Response;
  try {
    response = await fetcher(DASHBOARD_DATA_URL, { cache: "no-cache" });
  } catch (cause) {
    throw new DashboardDataError("manifest_unavailable", "Unable to fetch dashboard manifest", {
      cause
    });
  }
  if (!response.ok) {
    throw new DashboardDataError(
      "manifest_http",
      `Dashboard manifest returned HTTP ${response.status}`,
      { httpStatus: response.status }
    );
  }

  let manifestPayload: unknown;
  try {
    manifestPayload = await response.json();
  } catch (cause) {
    throw new DashboardDataError("manifest_invalid", "Dashboard manifest is not valid JSON", {
      cause
    });
  }

  let manifest: DashboardManifest;
  try {
    manifest = parseDashboardManifest(manifestPayload);
  } catch (cause) {
    throw new DashboardDataError(
      "manifest_invalid",
      "Dashboard manifest does not satisfy the schema-v2 contract",
      { cause }
    );
  }
  if (manifest.validityState === "invalid") {
    const details = manifest.validationIssues.map((issue) => issue.message).join("; ");
    throw new DashboardDataError(
      "dataset_invalid",
      details ? `Dashboard dataset is invalid: ${details}` : "Dashboard dataset is invalid",
      { manifest }
    );
  }

  const shards = await Promise.all(
    manifest.transactionShardUrls.map((shardName, shardIndex) =>
      loadCanonicalShard(fetcher, shardName, shardIndex)
    )
  );
  const transactions = shards.flat();

  try {
    validateLoadedDataset(manifest, transactions);
  } catch (cause) {
    throw new DashboardDataError(
      "dataset_validation_failed",
      "Loaded dashboard shards failed whole-dataset validation",
      { manifest, cause }
    );
  }

  let health: DatasetHealth;
  try {
    health = healthFromManifest(manifest, now);
  } catch (cause) {
    throw new DashboardDataError(
      "dataset_validation_failed",
      "Dashboard health metadata could not be computed",
      { manifest, cause }
    );
  }
  const indexes = buildDashboardIndexes(transactions);
  return { manifest, health, transactions, indexes };
}
